import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import IcomoonReact from 'icomoon-react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { Formik } from 'formik';
import TBDatePicker from '../../../shared/components/TBDatePicker/TBDatePicker';
import useRefetchRecurringData from '../components/presentational/hooks/useRefetchRecurringData';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import Text from '../../../shared/components/Text/Text';
import Spinner from '../../../shared/_TBComponents/Spinner';
import {
  convertRecurringItem,
  updateRecurringItemSchedule,
} from '../ducks/calendarActions';
import { showErrorToast } from '../../../shared/utils/toastManager';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;

  /* Phones: suppress the date-picker calendar popup - it overflows the screen
     and the keyboard covers it anyway; typing the date is more useful here.
     The input itself still works. Scoped to this modal. */
  @media (max-width: 480px) {
    .react-datepicker-popper {
      display: none !important;
    }
  }
`;

const SpinnerWrapper = styled('div')`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

const RemoveDivIconWrapper = styled('div')``;

const ClickHereDiv = styled('div')``;

const ModalTextTop = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;
const SubTitleText = styled(Text)`
  font-size: 16px;
  font-weight: 700;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const Subtext = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  margin-top: 5px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Subtext2 = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  margin-top: 15px;
  color: ${({ theme }) => theme.colors.textStrong};
`;

const TextContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-shrink: 0;
  margin-top: 30px;
`;

const TopTextAndX = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const SchedulingOptionsStack = styled('div')`
  display: flex;
  flex-direction: column;
`;

const TopSelectRow = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-top: 3px;
`;

const DatePickerStack = styled('div')`
  display: flex;
  flex-direction: column;
`;

const DatePickerRow = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-top: 3px;

  /* Phones: two 49%-wide date fields can't fit a phone (each picker has a
     170px min-width), so they overlap. Stack them instead. */
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const PickerAndText = styled('div')`
  display: flex;
  flex-direction: column;
  width: 49%;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const NewCalendarButton = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  width: 100%;
  height: 32px;
  padding: 6px 16px 6px 16px;
  background: ${({ theme }) => theme.colors.buttonPrimaryBg};
  color: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.isDisabled ? 0.6 : 1)};
  pointer-events: ${(props) => (props.isDisabled ? 'none' : 'auto')};
  user-select: none;
  border: 1px solid ${({ theme }) => theme.colors.text};
`;

const ManageCalendarsButton = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  width: 100%;
  height: 32px;
  padding: 6px 16px 6px 16px;
  color: ${({ theme }) => theme.colors.text};
  border-radius: 4px;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.isDisabled ? 0.6 : 1)};
  pointer-events: ${(props) => (props.isDisabled ? 'none' : 'auto')};
  user-select: none;
  border: 1px solid ${({ theme }) => theme.colors.text};
`;

const DaySelectors = styled('div')`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const DaySelector = styled('div')`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? props.theme.colors.text : props.theme.colors.surface)};
  color: ${(props) => (props.selected ? props.theme.colors.surface : props.theme.colors.text)};
  border: 1px solid ${(props) => (props.selected ? props.theme.colors.text : props.theme.colors.borderMedium)};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text};
  }
`;

const SelectDaysLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  margin-top: 15px;
  color: ${({ theme }) => theme.colors.textStrong};
`;

// Cadence enum: 0=Every Day, 1=Weekdays, 2=Days with Scheduled Workouts, 3=Custom.
const leftSelectOptions = [
  { name: 'Every Day', id: 0 },
  { name: 'Weekdays', id: 1 },
  { name: 'Days with Scheduled Workouts', id: 2 },
  { name: 'Custom', id: 3 },
];

const rightSelectOptions = [
  { name: 'Before Workout', id: 0 },
  { name: 'After Workout', id: 1 },
];

/** react-select paints its own light control and menu; read the theme so the
 * dropdowns follow Build's mode. Same recipe as SaveWorkoutModal's selects. */
const makeSelectStyles = (colors, width) => ({
  container: (provided) => ({
    ...provided,
    width,
  }),
  control: (provided, state) => ({
    ...provided,
    fontFamily: 'Nunito Sans',
    background: colors.surfaceInput,
    borderColor: state.isFocused ? colors.hover : colors.borderLight,
    boxShadow: 'none',
    '&:hover': {
      borderColor: state.isFocused ? colors.hover : colors.borderLight,
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: colors.text,
  }),
  input: (provided) => ({
    ...provided,
    color: colors.text,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: colors.textSecondary,
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    backgroundColor: colors.borderLight,
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: colors.textSecondary,
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: colors.surfaceElevated,
    color: colors.text,
  }),
  option: (provided, state) => ({
    ...provided,
    paddingLeft: state.data.parentId ? 35 : 15,
    backgroundColor: state.isFocused ? colors.surfaceSecondary : colors.surfaceElevated,
    color: state.isSelected ? colors.textTertiary : colors.text,
    '&:active': {
      backgroundColor: colors.surfaceSecondary,
    },
  }),
});

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_LABELS = {
  sunday: 'Su',
  monday: 'M',
  tuesday: 'T',
  wednesday: 'W',
  thursday: 'Th',
  friday: 'F',
  saturday: 'Sa',
};
const DAY_INDEX = DAY_KEYS.reduce((acc, k, i) => { acc[k] = i; return acc; }, {});
const emptyDays = () => DAY_KEYS.reduce((acc, k) => { acc[k] = false; return acc; }, {});

const buildInitialDays = (initialSchedule) => {
  if (!initialSchedule || initialSchedule.cadence !== 3 || !initialSchedule.customDays) {
    return emptyDays();
  }
  const base = emptyDays();
  initialSchedule.customDays.forEach((i) => {
    if (DAY_KEYS[i]) base[DAY_KEYS[i]] = true;
  });
  return base;
};

const SetRecurringItemModal = ({ closeModal, setIsCloseLocked }) => {
  const { colors } = useTheme();
  const customStyles = useMemo(() => makeSelectStyles(colors, '57%'), [colors]);
  const customStyles2 = useMemo(() => makeSelectStyles(colors, '41%'), [colors]);
  const refetchRecurringData = useRefetchRecurringData();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const modalData = useSelector((state) => state.calendar.data.modalData);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const calendarPickerFirstMonth = useSelector(
    (state) => state.calendar.data.firstMonthInPicker,
  );
  const dispatch = useDispatch();

  const currentMonth = moment(selectedDate).startOf('month').toDate();
  const nextMonth = moment(selectedDate).add(1, 'month').startOf('month').toDate();
  const initialSchedule = modalData?.schedule;

  const initialValues = {
    cadenceId: initialSchedule?.cadence ?? 0,
    positionId: initialSchedule?.position ?? 0,
    startDate: initialSchedule?.startDate
      ? moment(initialSchedule.startDate).format('YYYY-MM-DD')
      : moment(selectedDate).format('YYYY-MM-DD'),
    endDate: initialSchedule?.endDate
      ? moment(initialSchedule.endDate).format('YYYY-MM-DD')
      : null,
    selectedDays: buildInitialDays(initialSchedule),
  };

  const dispatchDate = () => moment(modalData?.effectiveDate || selectedDate).format('YYYY-MM-DD');

  const buildSchedulePayload = (values) => {
    const item = {
      cadence: values.cadenceId,
      position: values.positionId,
      startDate: values.startDate,
      ...(values.endDate ? { endDate: values.endDate } : {}),
    };
    if (values.cadenceId === 3) {
      item.customDays = DAY_KEYS
        .filter((key) => values.selectedDays[key])
        .map((key) => DAY_INDEX[key]);
    }
    return item;
  };

  const refetchPayload = () => ({
    currentUser,
    currentMonth,
    nextMonth,
    selectedReduxCalendar,
    selectedReduxAthlete,
    closeModal,
    calendarPickerFirstMonth,
  });

  const handleFormikSubmit = (values, { setSubmitting }) => {
    if (!values.startDate) {
      showErrorToast('Please select a start date');
      setSubmitting(false);
      return;
    }
    // Intentional new behavior: Custom cadence with zero days has no meaning —
    // previously the form silently accepted `customDays: []`. Block with a toast.
    if (values.cadenceId === 3) {
      const anySelected = Object.values(values.selectedDays).some(Boolean);
      if (!anySelected) {
        showErrorToast('Please select at least one day');
        setSubmitting(false);
        return;
      }
    }

    setIsCloseLocked(true);

    if (modalData?.preSave && !modalData?.isRecurring) {
      closeModal();
      setSubmitting(false);
      return;
    }

    const updateItem = buildSchedulePayload(values);

    if (!modalData?.preSave && !modalData?.isRecurring) {
      dispatch(convertRecurringItem(
        currentUser.accountCode,
        updateItem,
        modalData.assignedId,
        modalData.itemIndex,
        dispatchDate(),
        {
          onSuccess: () => refetchRecurringData(refetchPayload()),
          onError: () => {
            setIsCloseLocked(false);
            setSubmitting(false);
          },
        },
      ));
      return;
    }

    if (!modalData?.preSave && modalData?.isRecurring) {
      dispatch(updateRecurringItemSchedule(
        currentUser.accountCode,
        updateItem,
        modalData.assignedId,
        modalData.itemIndex,
        dispatchDate(),
        modalData.isRecurring,
        {
          onSuccess: () => refetchRecurringData(refetchPayload()),
          onError: () => {
            setIsCloseLocked(false);
            setSubmitting(false);
            closeModal();
          },
        },
      ));
      return;
    }

    // preSave && isRecurring -> matches the old fall-through.
    closeModal();
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormikSubmit}
      render={({
        values, setFieldValue, handleSubmit, isSubmitting,
      }) => {
        const handleStartChange = (date) => {
          if (!date) {
            setFieldValue('startDate', null);
            return;
          }
          const m = moment(date);
          setFieldValue('startDate', m.format('YYYY-MM-DD'));
          if (values.endDate && m.isAfter(moment(values.endDate))) {
            setFieldValue('endDate', null);
          }
        };
        const handleEndChange = (date) => {
          if (!date) {
            setFieldValue('endDate', null);
            return;
          }
          setFieldValue('endDate', moment(date).format('YYYY-MM-DD'));
        };
        const toggleDay = (day) => {
          setFieldValue('selectedDays', {
            ...values.selectedDays,
            [day]: !values.selectedDays[day],
          });
        };

        const selectedCadence = leftSelectOptions.find((o) => o.id === values.cadenceId);
        const selectedPosition = rightSelectOptions.find((o) => o.id === values.positionId);

        return (
          <ModalContainer>
            {isSubmitting ? (
              <SpinnerWrapper>
                <Spinner large />
              </SpinnerWrapper>
            ) : (
              <>
                <div className='remove-div'>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <TextContainer>
                      <TopTextAndX>
                        <ModalTextTop>
                          {!modalData?.preSave && modalData?.isRecurring
                            ? 'Update Recurring Schedule'
                            : 'Recurring Schedule'}
                        </ModalTextTop>
                        <RemoveDivIconWrapper
                          className='remove-div-icon'
                          onClick={() => closeModal()}
                          style={{ cursor: 'pointer' }}
                        >
                          <IcomoonReact
                            iconSet={iconSet}
                            size={15}
                            icon='remove'
                            color={colors.textSecondary}
                          />
                        </RemoveDivIconWrapper>
                      </TopTextAndX>
                      <SubTitleText>{modalData?.title}</SubTitleText>
                      <Subtext>
                        All changes made to a recurring item will be reflected on every day
                        that it is scheduled
                        (including past instances).
                        {' '}
                        <ClickHereDiv
                          style={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            color: '#007FAE',
                          }}
                          onClick={() => window.open('https://support.teambuildr.com/article/t5talti5mh-recurring-workout-items', '_blank', 'noopener,noreferrer')}
                        >
                          Click here to learn more.
                        </ClickHereDiv>
                      </Subtext>
                    </TextContainer>
                  </div>
                </div>
                <SchedulingOptionsStack>
                  <Subtext2>Scheduling Options</Subtext2>
                  <TopSelectRow>
                    <Select
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                      options={leftSelectOptions}
                      onChange={(option) => setFieldValue('cadenceId', option.id)}
                      value={selectedCadence}
                      isSearchable
                      styles={customStyles}
                      placeholder='Select an option'
                      autoFocus
                    />
                    <Select
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                      options={rightSelectOptions}
                      onChange={(option) => setFieldValue('positionId', option.id)}
                      value={selectedPosition}
                      isSearchable
                      styles={customStyles2}
                      placeholder='Select an option'
                    />
                  </TopSelectRow>
                </SchedulingOptionsStack>
                <DatePickerStack>
                  <DatePickerRow>
                    <PickerAndText>
                      <Subtext2>Start Date</Subtext2>
                      <TBDatePicker
                        activeDate={values.startDate || ''}
                        setActiveDate={handleStartChange}
                        additionalStyles={{ margin: '0', maxWidth: '100%' }}
                        showDropShadow={false}
                        maxDate={null}
                        showIcon={false}
                      />
                    </PickerAndText>
                    <PickerAndText>
                      <Subtext2>End Date</Subtext2>
                      <TBDatePicker
                        activeDate={values.endDate || ''}
                        setActiveDate={handleEndChange}
                        additionalStyles={{ margin: '0', maxWidth: '100%' }}
                        showDropShadow={false}
                        minDate={values.startDate
                          ? new Date(
                            ...values.startDate.split('-').map((v, i) => (i === 1 ? Number(v) - 1 : Number(v))),
                          )
                          : null}
                        maxDate={null}
                        showIcon={false}
                      />
                    </PickerAndText>
                  </DatePickerRow>
                </DatePickerStack>
                {values.cadenceId === 3 && (
                  <div>
                    <SelectDaysLabel>Select Days</SelectDaysLabel>
                    <DaySelectors>
                      {DAY_KEYS.map((day) => (
                        <DaySelector
                          key={day}
                          selected={values.selectedDays[day]}
                          onClick={() => toggleDay(day)}
                        >
                          {DAY_LABELS[day]}
                        </DaySelector>
                      ))}
                    </DaySelectors>
                  </div>
                )}
              </>
            )}
            <ButtonsContainer>
              <NewCalendarButton
                isDisabled={isSubmitting}
                onClick={() => {
                  if (isSubmitting) return;
                  handleSubmit();
                }}
              >
                SAVE
              </NewCalendarButton>
              <ManageCalendarsButton
                isDisabled={isSubmitting}
                onClick={() => {
                  if (isSubmitting) return;
                  closeModal();
                }}
              >
                CANCEL
              </ManageCalendarsButton>
            </ButtonsContainer>
          </ModalContainer>
        );
      }}
    />
  );
};

SetRecurringItemModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  setIsCloseLocked: PropTypes.func.isRequired,
};

export default SetRecurringItemModal;
